import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2026-09-21 15:25:56 +0200 (Mon, 21 Sep 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 165779 $
//  Purpose   : presentation wiki templates
// =============================================================================
// ---Third-party imports
import React, { createContext, useRef } from 'react';
import { Style } from 'react-head';
import clsx from 'clsx';
import { Card, CardContent, CardHeader, Header, Message, Dropdown } from 'semantic-ui-react';
// ---Framework imports
import { Sel } from 'uix/registries';
import log from 'uix/logging';
import { windowDevicePixelRatio } from 'uix/utils';
import { isDigit, strip, sum, range, keys, isNil, groupBy, sortBy, dict, mapItems } from 'ldash';
import { TagMatcher, BOOL_STR, useWikiContext, schemaifyAttributes } from 'uix/wiki/utils';
import { useEffect, useSelector, useCallback, useState, useInView, useDimensions, useDebounce, useApi } from 'uix/hooks';
import { VIcon } from 'uix/graphics';
import { TagCloud } from 'uix/components/tagcloud';
import { RawLink, Link } from 'uix/components/links';
import { Button } from 'uix/components';
// ---Local imports
import S from './templates.css';
import GlobalS from './index.css';
import { TextTicker, AudioTicker, Showcase, AiImage, PageViewer, Modal, MediaPlayer, GhostImage } from './components';
import { useCaptionLink, usePageIdMdCaption, useSwipeGesture, useWidgetSchema, useModalWidSync } from './hooks';
import { validateMeta } from './utils';
import { GndPopup } from '../gndo/components';
import { AutoPortal, XPopup, AccessWarning } from 'uix/components';
import { InViewTransition } from './components';
// ---Exports
const PNG_CACHE = {};
const createPNGImageDataURI = ({ width, height, backgroundColor = 'white', text, cacheKey })=>{
    if (cacheKey && PNG_CACHE[cacheKey]) {
        return PNG_CACHE[cacheKey];
    }
    // Create a canvas element with the specified width and height
    let canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    // Get the canvas 2D context
    let ctx = canvas.getContext('2d');
    // Draw a background color if needed (replace 'white' with any color)
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    if (text) {
        // Draw text on the canvas
        ctx.font = '10px sans-serif';
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, width / 2, height / 2);
    }
    // Convert the canvas content to a PNG data URI
    let res = canvas.toDataURL('image/png');
    if (cacheKey) {
        PNG_CACHE[cacheKey] = res;
    }
    return res;
};
const useWebcacheWidth = (renderedWidth)=>{
    let wcSizes = useSelector((root)=>root.config.instance.webcache.sizes);
    renderedWidth = renderedWidth * windowDevicePixelRatio;
    // wc-width 0 (org-width) will be ignored
    let bestFitWidth = wcSizes.filter((w)=>w <= renderedWidth && w <= 2000).at(-1);
    bestFitWidth = bestFitWidth || 504;
    // never decrease
    let [maxWidth, setMaxWidth] = useState(0);
    useEffect(()=>{
        if (bestFitWidth > maxWidth) setMaxWidth(bestFitWidth);
    }, [
        bestFitWidth
    ]);
    return Math.max(bestFitWidth, maxWidth);
};
const useImageInfo = ({ id, poster, imgWidth, imgHeight, boxId, boxWidth, boxHeight, requestedWidth, renderedWidth })=>{
    let apiurl = useSelector(Sel.apiurl);
    let [isError, setIsError] = useState(false);
    let [isLoaded, setIsLoaded] = useState(false);
    let wikictx = useWikiContext();
    let { assetsById = {} } = wikictx;
    // XXX: this "choose requested resolution by tracking dimensions"
    // does not really work: it's hard to prevent that resolution changing triggers a dimension change
    let wcWidth = useWebcacheWidth(renderedWidth);
    if (!requestedWidth) {
        // if no external width given - pin down resolution (see XXX above)
        wcWidth = 1000;
    }
    let imgsrc = null;
    let aspectRatio = undefined;
    // XXX: remove dependency on wikiContext
    let assetInfo = assetsById[id];
    if (assetInfo && assetInfo.datatype == 3) {
        if (assetInfo.page.crop) {
            // cropped image
            let { width, height } = assetInfo.page.crop;
            aspectRatio = height / width;
        } else {
            // full image
            let { width, height } = assetInfo.page;
            aspectRatio = height / width;
        }
    }
    // the prefered data transport channel
    if (boxId && boxWidth && boxHeight) {
        // cropped image
        aspectRatio = boxHeight / boxWidth;
    } else if (imgWidth, imgHeight) {
        // full image
        aspectRatio = imgHeight / imgWidth;
    }
    let createErrorImage = (text)=>createPNGImageDataURI({
            text,
            width: wcWidth,
            height: Math.floor(wcWidth * aspectRatio),
            backgroundColor: '#FFAAAACC'
        });
    if (renderedWidth === -1 && !!requestedWidth && !poster) {
        // useDimensions needs another rendering pass
        // so use a placeholder first
        imgsrc = createPNGImageDataURI({
            width: 100,
            height: Math.floor(100 * aspectRatio),
            backgroundColor: '#00000000',
            cacheKey: aspectRatio
        });
    } else if (isError) {
        imgsrc = createErrorImage(`error: ${id}`.slice(0, 70));
    } else if (id) {
        // `id` is the legacy/asset-context image channel. It depends on assetsById,
        // so cross-linked resolved Graphics should prefer `poster` instead because
        // their asset may not be in the current WikiPane.
        imgsrc = `/download/webcache/${wcWidth}/${id}`;
        if (assetInfo) {
            let otid = assetInfo.otlink || id // either linked or physical page
            ;
            if (assetInfo.datatype == 3) {
                if (assetInfo.page?.crop) {
                    let { opbid } = assetInfo.page.crop;
                    imgsrc = `/image/pagebox/${opbid}/${wcWidth}`;
                } else {
                    imgsrc = `/download/webcache/${wcWidth}/${otid}`;
                }
            } else if (assetInfo.datatype == 9 && assetInfo.subtype == 2) {
                // webimage
                imgsrc = `/download/webresource/${otid}`;
            }
        }
        imgsrc = apiurl.href(imgsrc);
    } else if (poster) {
        // Resolved webresource URL. This is the stable channel for Graphic webimage
        // assets, especially when the Graphic is referenced from outside this widget tree.
        imgsrc = apiurl.href(poster);
    } else {
        imgsrc = createErrorImage('no id');
    }
    let onError = useCallback(()=>setIsError(true), []);
    let onLoad = useCallback(()=>setIsLoaded(true), []);
    return {
        imgsrc,
        aspectRatio,
        onError,
        onLoad,
        isLoaded,
        isError,
        awstate: assetInfo?.awstate
    };
};
const Image = ({ id, poster, width, alt, style, onClick, imgWidth, imgHeight, imageLink, boxId, boxWidth, boxHeight, focusPoints = [], mobile, applyTemplates, fpNavigation })=>{
    let [dimensions, ref] = useDimensions({
        defaults: {
            width: -1,
            height: -1
        }
    });
    let [renderedWidth] = useDebounce(dimensions.width, 500, {
        leading: true
    });
    let { imgsrc, aspectRatio, onError, onLoad, isLoaded, isError, awstate } = useImageInfo({
        id,
        poster,
        imgWidth,
        imgHeight,
        boxId,
        boxWidth,
        boxHeight,
        requestedWidth: width,
        renderedWidth
    });
    let [activeContentFP, setActiveContentFP] = useState(undefined);
    let [currentFPIndex, setCurrentFPIndex] = useState(()=>{
        let idx = focusPoints.findIndex((fp)=>fp.attrib?.initialActive === 'true');
        return idx > -1 ? idx : undefined;
    });
    //FocusPoint related
    let [zoomState, setZoomState] = useState({
        active: false,
        transform: '',
        duration: currentFPIndex !== undefined ? 0 : 2,
        content: ''
    });
    let [validFPIndex, setValidFPIndex] = useState(0);
    let aspectR = aspectRatio ? `1 / ${aspectRatio}` : '16/9';
    let w = imgWidth || boxWidth ? `${imgWidth || boxWidth}px` : '100%';
    let h = imgHeight || boxHeight ? `${imgHeight || boxHeight}px` : 'auto';
    let resetZoom = ()=>{
        if (zoomState.active) {
            setCurrentFPIndex(undefined);
        }
    };
    const renderImg = (imgStyles = {})=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                !isLoaded && /*#__PURE__*/ _jsx(GhostImage, {
                    style: {
                        ...style,
                        aspectRatio: aspectR,
                        ...imgStyles.position ? {
                            position: 'absolute',
                            top: 0,
                            left: 0
                        } : {}
                    },
                    width: w,
                    height: h
                }),
                /*#__PURE__*/ _jsx("img", {
                    ref: ref,
                    style: {
                        ...style,
                        ...imgStyles,
                        ...isLoaded ? {
                            opacity: 1
                        } : {
                            opacity: 0,
                            position: 'absolute',
                            width: '1px',
                            height: '1px'
                        }
                    },
                    src: imgsrc,
                    alt: alt,
                    onClick: onClick,
                    onLoad: onLoad,
                    onError: onError,
                    loading: "lazy"
                })
            ]
        });
    };
    const wrapImg = (content)=>imageLink ? /*#__PURE__*/ _jsx("a", {
            style: {
                cursor: 'pointer'
            },
            href: imageLink,
            children: content
        }) : content;
    let zoomFocusIndices = focusPoints.flatMap((fp, i)=>{
        let hasZoomFocus = fp.attrib?.zoomfocus === 'true';
        let hasContent = getChildrenByTag(fp.children, 'FocusPointContent', 1).length > 0;
        return hasZoomFocus || hasContent ? [
            i
        ] : [];
    });
    let totalFocus = zoomFocusIndices.length;
    useEffect(()=>{
        if (currentFPIndex !== undefined) {
            setValidFPIndex(currentFPIndex);
        }
    }, [
        currentFPIndex
    ]);
    let nextFocus = (e)=>{
        e.stopPropagation();
        setCurrentFPIndex((prev)=>{
            let currentIdx = zoomFocusIndices.indexOf(prev);
            let nextIdx = (currentIdx + 1) % totalFocus;
            return zoomFocusIndices[nextIdx];
        });
    };
    let prevFocus = (e)=>{
        e.stopPropagation();
        setCurrentFPIndex((prev)=>{
            let currentIdx = zoomFocusIndices.indexOf(prev);
            let prevIdx = (currentIdx - 1 + totalFocus) % totalFocus;
            return zoomFocusIndices[prevIdx];
        });
    };
    let currentZoomIdx = zoomFocusIndices.indexOf(validFPIndex);
    let initPulseFP = zoomState.active ? focusPoints.find((fp, i)=>i === currentFPIndex && fp.attrib.zoomfocus === 'true' && fp.attrib.initialActive === 'true') : null;
    let showInitPulse = initPulseFP != null;
    let fPNav = null;
    if (fpNavigation === 'none' || totalFocus <= 1) {
        fPNav = null;
    } else if (fpNavigation === 'carousel') {
        fPNav = /*#__PURE__*/ _jsxs("div", {
            className: clsx(S.carouselNav, zoomState.active && S.show, mobile && S.mobile),
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    className: clsx(S.carouselButton, S.prev),
                    disabled: zoomState.active && currentZoomIdx <= 0,
                    onClick: currentZoomIdx > 0 ? prevFocus : undefined,
                    size: "small",
                    inverted: true,
                    icon: "chevron left"
                }),
                /*#__PURE__*/ _jsx(Button, {
                    className: clsx(S.carouselButton, S.next),
                    onClick: currentZoomIdx < totalFocus - 1 ? nextFocus : undefined,
                    disabled: zoomState.active && currentZoomIdx >= totalFocus - 1,
                    size: "small",
                    inverted: true,
                    icon: "chevron right"
                })
            ]
        });
    } else {
        fPNav = /*#__PURE__*/ _jsxs("div", {
            className: clsx(S.fPCarouselNav, zoomState.active && S.show, mobile && S.mobile),
            children: [
                /*#__PURE__*/ _jsx("a", {
                    onClick: currentZoomIdx > 0 ? prevFocus : undefined,
                    children: /*#__PURE__*/ _jsx(VIcon, {
                        className: clsx(S.prev, currentZoomIdx <= 0 && S.disabled),
                        name: "arrow left"
                    })
                }),
                /*#__PURE__*/ _jsxs("span", {
                    children: [
                        currentZoomIdx + 1,
                        " / ",
                        totalFocus
                    ]
                }),
                /*#__PURE__*/ _jsx("a", {
                    onClick: currentZoomIdx < totalFocus - 1 ? nextFocus : undefined,
                    children: /*#__PURE__*/ _jsx(VIcon, {
                        className: clsx(S.prev, currentZoomIdx >= totalFocus - 1 && S.disabled),
                        name: "arrow right"
                    })
                })
            ]
        });
    }
    return focusPoints.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            fpNavigation !== 'carousel' && !mobile && fPNav,
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    ...style,
                    ...initPulseFP?.attrib?.pulseColor ? {
                        '--widget_fP_bgColor': initPulseFP.attrib.pulseColor
                    } : {},
                    ...showInitPulse ? {
                        '--pulse-delay': `${Math.max(zoomState.duration, 1.2)}s`
                    } : {}
                },
                className: clsx(S.focusGraphicCnt, showInitPulse && S.initPulse),
                onClick: resetZoom,
                children: [
                    (mobile || fpNavigation === 'carousel') && fPNav,
                    wrapImg(/*#__PURE__*/ _jsxs(AccessWarning, {
                        id: id,
                        awstate: awstate,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    width: '100%',
                                    height: '100%',
                                    transformOrigin: zoomState.origin,
                                    transform: zoomState.active ? zoomState.transform : 'none',
                                    transition: zoomState.duration > 0 ? `transform ${zoomState.duration}s ease-in-out` : 'none',
                                    cursor: imageLink ? 'pointer' : zoomState.active ? 'pointer' : 'default'
                                },
                                children: renderImg()
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                style: {
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%'
                                },
                                id: `focus-mid-${id}`
                            })
                        ]
                    })),
                    isLoaded && /*#__PURE__*/ _jsx(FocusPoints, {
                        id: id,
                        focusPoints: focusPoints,
                        dimensions: dimensions,
                        aspectRatio: aspectRatio,
                        applyTemplates: applyTemplates,
                        setZoomState: setZoomState,
                        zoomState: zoomState,
                        setCurrentFPIndex: setCurrentFPIndex,
                        currentFPIndex: currentFPIndex,
                        setActiveContentFP: setActiveContentFP
                    })
                ]
            }),
            zoomState.active && activeContentFP && applyTemplates(activeContentFP)
        ]
    }) : wrapImg(/*#__PURE__*/ _jsx(AccessWarning, {
        id: id,
        awstate: awstate,
        onClick: onClick,
        children: renderImg()
    }));
};
const ExpandButton = ({ open, hasText, onClick, link })=>/*#__PURE__*/ _jsx("span", {
        onClick: onClick,
        className: clsx(S.expandButton, open && S.open),
        children: hasText ? /*#__PURE__*/ _jsxs("span", {
            className: S.plusMinusIcon,
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: clsx(S.line, S.horiz)
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: clsx(S.line, S.vert)
                })
            ]
        }, link) : /*#__PURE__*/ _jsx("a", {
            target: "_blank",
            className: S.link,
            href: link,
            children: "→ zum Glossareintrag"
        })
    });
export const GlossaryList = TagMatcher('GlossaryList', ({ parentEl, childEls, applyTemplates, ...attrs })=>{
    let apiurl = useSelector(Sel.apiurl);
    let { data, loading } = useApi('glossary/getGlossaryList');
    const [openIdx, setOpenIdx] = useState(null);
    if (loading) return /*#__PURE__*/ _jsx("div", {
        style: {
            width: '300px'
        },
        children: "Lade Glossar..."
    });
    let glossary = Array.isArray(data?.glossaryList) ? sortBy(data.glossaryList, (e)=>(e.caption || '').toLocaleLowerCase()) : [];
    const hasContent = (htmlString)=>{
        // checks if definition htmlString is filled with content
        if (!htmlString) return false;
        const textContent = htmlString.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        return textContent.length > 0;
    };
    return /*#__PURE__*/ _jsx("div", {
        className: S.glossaryList,
        children: glossary.map((entry, idx)=>{
            const isOpen = openIdx === idx;
            const hasText = hasContent(entry.definition);
            let link = apiurl.href('/glossary/view/' + entry.otid);
            return /*#__PURE__*/ _jsxs("div", {
                className: S.entry,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.header, isOpen && S.open, hasText ? S.hasText : S.noText),
                        onClick: ()=>hasText && setOpenIdx(isOpen ? null : idx),
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: S.caption,
                                children: entry.caption
                            }),
                            /*#__PURE__*/ _jsx(ExpandButton, {
                                link: link,
                                open: isOpen,
                                hasText: hasText,
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    hasText && setOpenIdx(isOpen ? null : idx);
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.content, (!isOpen || !hasText) && S.collapsed),
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: S.hidden,
                                dangerouslySetInnerHTML: {
                                    __html: entry.definition
                                }
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: S.link,
                                children: /*#__PURE__*/ _jsx("a", {
                                    target: "_blank",
                                    href: link,
                                    children: "→ mehr lesen"
                                })
                            })
                        ]
                    })
                ]
            }, entry.otid);
        })
    });
}, {}, '');
export const GraphicMatcher = TagMatcher('Graphic', ({ parentEl, childEls, applyTemplates, ...attrs })=>{
    let schema = useWidgetSchema();
    let meta = validateMeta('Graphic', attrs, schema);
    let { // source
    external, hideLink, id, imageLink, link, linkid, url, // resolved image source
    imgWidth, imgHeight, // resolved pagebox crop source
    boxId, boxWidth, boxHeight, // resolved webresource source
    poster, // content
    alt, title, caption, showCaption, // styling
    style, fixSize, height, position, width, marginTop, marginRight, marginBottom, marginLeft, float, // features
    interact, framed, frameColor, fpNavigation } = meta;
    let { mobile } = useWikiContext();
    let apiurl = useSelector(Sel.apiurl);
    let isGroupChild = parentEl?.tag === 'GraphicGroup';
    let groupType = parentEl.attrib?.type;
    let focusPoints = childEls?.filter((e)=>e.tag == 'FocusPoint');
    let modalTypeIcon = null;
    let imgStyle = {
        objectPosition: position || 'top'
    };
    let isRelativeW = typeof width === 'string' && width.endsWith('%');
    let w = isRelativeW ? '100%' : width;
    let isRelativeH = typeof height === 'string' && height.endsWith('%');
    let h = isRelativeH ? `${parseFloat(height)}vh` : height;
    let graphicW = width ? isRelativeW ? width : 'max-content' : '100%';
    if (h && !width) graphicW = 'max-content';
    let maxW = '100%';
    if (mobile && !fixSize) {
        w = '100%';
        h = 'auto';
        graphicW = '100%';
    }
    let graphicH = null;
    if (isGroupChild) {
        graphicH = h;
        graphicW = null;
        if ([
            'carousel',
            'masonry'
        ].includes(groupType)) {
            graphicW = null;
            w = null;
        }
        if (groupType === 'flex-fill') {
            maxW = width;
            w = '100%';
        }
        let pAttr = parentEl.attrib || {};
        interact = pAttr.interact || interact;
        graphicH = h || pAttr.height;
        framed = pAttr.framed || framed;
        frameColor = pAttr.frameColor || frameColor;
    }
    imgStyle = {
        ...imgStyle,
        width: w,
        height: h,
        ...style
    };
    let modalEl = childEls.filter((e)=>e.tag == 'Modal').at(0);
    let isModalMode = interact == 'zoomModal' || !isNil(modalEl);
    let onGraphicClick = isModalMode ? ()=>openModal() : undefined;
    if (isModalMode) {
        modalTypeIcon = ({
            zoom: 'zoom',
            viewer: 'book',
            text: 'info',
            more: 'plus'
        })[modalEl?.attrib?.type] || 'zoom';
    }
    let imgSourceProps = {
        imgWidth,
        imgHeight,
        boxId,
        boxWidth,
        boxHeight,
        poster
    };
    // a numeric imageLink is an otid, route it like linkid instead of a raw href
    let imageLinkOtid = imageLink && isDigit(imageLink) ? imageLink : undefined;
    let effectiveLinkId = linkid || imageLinkOtid;
    let img = /*#__PURE__*/ _jsx(Image, {
        id: id,
        width: width,
        style: imgStyle,
        alt: alt || title,
        imageLink: imageLinkOtid ? undefined : imageLink,
        onClick: onGraphicClick,
        focusPoints: focusPoints,
        mobile: mobile,
        applyTemplates: applyTemplates,
        fpNavigation: fpNavigation,
        ...imgSourceProps
    });
    if (effectiveLinkId) {
        img = /*#__PURE__*/ _jsx(Link, {
            id: effectiveLinkId,
            path: !effectiveLinkId ? url : undefined,
            onClicked: onGraphicClick,
            external: external,
            target: external ? '_blank' : undefined,
            children: img
        });
    } else if (link) {
        img = /*#__PURE__*/ _jsx(RawLink, {
            onClick: onGraphicClick,
            href: apiurl.href(link),
            target: external ? '_blank' : undefined,
            children: img
        });
    }
    let captionLink = /*#__PURE__*/ _jsx("div", {
        className: clsx(S.description, showCaption && S.showCaption),
        children: useCaptionLink(caption, linkid, url, id, !isGroupChild && true, hideLink)
    });
    let modalCaption = /*#__PURE__*/ _jsx("div", {
        className: clsx(S.description, S.inModal, mobile && S.mobile, showCaption && S.showCaption),
        children: useCaptionLink(caption, linkid, url, id, true, hideLink)
    });
    let graphicStyle = {
        // avoid mixing margin shorthand/longhand
        ...!style?.margin && {
            marginBottom,
            marginLeft: !mobile && marginLeft,
            marginRight: !mobile && marginRight,
            marginTop
        },
        height: graphicH,
        width: graphicW,
        maxWidth: maxW,
        float,
        borderColor: frameColor
    };
    let res = /*#__PURE__*/ _jsxs("div", {
        refid: attrs.refid,
        "data-modal-refid": modalEl?.attrib?.refid,
        style: {
            ...graphicStyle,
            ...style,
            float: mobile ? 'none' : float
        },
        className: clsx(S.graphic, focusPoints.length > 1 && S.focusParent, framed && S.framed, isModalMode && S.modalMode, mobile && S.mobile, 'presGraphic'),
        title: title,
        children: [
            img,
            (imageLink || isModalMode || isGroupChild && showCaption !== false) && /*#__PURE__*/ _jsx("div", {
                className: clsx(S.clickable, isGroupChild && S.inParent)
            }),
            isModalMode && /*#__PURE__*/ _jsx(_Fragment, {
                children: /*#__PURE__*/ _jsx(Button, {
                    size: "large",
                    onClick: onGraphicClick,
                    className: S.zoomButton,
                    icon: modalTypeIcon,
                    circle: true,
                    inverted: true
                })
            }),
            showCaption !== false && captionLink
        ]
    });
    let [modalOpen, setModalOpen] = useState(false);
    let { openModal, closeModal } = useModalWidSync(modalEl?.attrib?.refid ?? attrs.refid, modalOpen, setModalOpen);
    if (isModalMode) {
        let modalContent = null;
        let bgid = null;
        let scrolling = false;
        if (interact == 'zoomModal') {
            modalContent = /*#__PURE__*/ _jsx(PageViewer, {
                mobile: mobile,
                optionSet: "grid",
                otids: [
                    id
                ],
                height: "100%",
                zoomControls: "inOut"
            });
        } else if (!isNil(modalEl)) {
            modalContent = applyTemplates(modalEl.children.find((e)=>e.tag == 'ModalContent'));
            bgid = modalEl.attrib.bgid;
            scrolling = true;
            if (modalEl.attrib.hideCaption) {
                modalCaption = null;
            }
        }
        if (modalOpen) {
            //XXX: rendering image before modal prevent image unmounting on modal rendering
            res = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    res,
                    /*#__PURE__*/ _jsx(Modal, {
                        mobile: mobile,
                        scrolling: scrolling,
                        open: modalOpen,
                        onClose: closeModal,
                        imageId: bgid,
                        caption: modalCaption,
                        fullwidth: (interact == 'zoomModal') ?? true,
                        ...modalEl?.attrib,
                        children: modalContent
                    })
                ]
            });
        }
    }
    return res;
}, 'Render a Graphic');
// Recursively collects all children with a given tag,
// compensates for nested wrappers like <Language>
let getChildrenByTag = (children, tagName, maxDepth, depth = 0, found = [])=>{
    if (depth > maxDepth) return found;
    for (let child of children){
        if (child.tag === tagName) {
            found.push(child);
        }
        if (child.children) {
            getChildrenByTag(child.children, tagName, maxDepth, depth + 1, found);
        }
    }
    return found;
};
// Checks if a given tag exists within the child elements (recursively)
// compensates for nested wrappers like <Language>
let hasChildTag = (children, tagName, maxDepth = 1, depth = 0)=>{
    if (depth > maxDepth) return false;
    return children.some((child)=>child.tag === tagName || child.children && hasChildTag(child.children, tagName, maxDepth, depth + 1));
};
const FocusPoints = ({ focusPoints, id, dimensions, aspectRatio, applyTemplates, setZoomState, zoomState, currentFPIndex, setCurrentFPIndex, setActiveContentFP })=>{
    let [duration, setDuration] = useState(2);
    let [isInitialActive, setIsInitialActive] = useState(false);
    let prevFPIndexRef = useRef(currentFPIndex);
    const zoomToFocusPoint = (fP, id, isInitial = false)=>{
        if (!fP) {
            if (zoomState?.transform == 'none') {
                setDuration(0.1);
            } else {
                setDuration(zoomState.duration || 2);
            }
            // reset Zoom
            setZoomState((prev)=>({
                    ...prev,
                    transform: 'none',
                    active: false,
                    content: undefined,
                    duration: prev.duration === 0 ? 2 : prev.duration || 2
                }));
            setActiveContentFP(undefined);
            return;
        }
        let fPContent = getChildrenByTag(fP.children, 'FocusPointContent', 2)[0];
        let hasContent = fPContent !== undefined;
        let contentNoZoom = hasContent && fP.attrib.zoomfocus !== 'true';
        if (hasContent) {
            if (contentNoZoom) {
                if (!zoomState.active || zoomState.transform === 'none') {
                    fPContent.attrib.duration = isInitial ? 0 : 0.3;
                    setDuration(isInitial ? 0 : 0.3);
                } else {
                    fPContent.attrib.duration = isInitial ? 0 : fP.attrib.duration || zoomState.duration || 2;
                    setDuration(fPContent.attrib.duration);
                }
            } else {
                fPContent.attrib.duration = isInitial ? 0 : fP.attrib.duration || zoomState.duration || 2;
                setDuration(fPContent.attrib.duration);
            }
        }
        // If image is taller than viewport, scroll to center first
        // Ensures the focus point is visible after the animation
        let centerAnchor = document.getElementById(`focus-mid-${id}`);
        let fpAnchor = document.getElementById(`focus-${id}`);
        let viewportHeight = window.innerHeight;
        let largeVerticalImage = dimensions.height > viewportHeight + 300;
        let timeout = 0;
        if (largeVerticalImage && !isInitial) {
            let shouldUseFP = contentNoZoom && zoomState.active;
            let anchor = shouldUseFP ? fpAnchor : centerAnchor;
            if (anchor) {
                anchor?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
                timeout = 200;
            }
        }
        if (contentNoZoom) {
            setTimeout(()=>{
                setZoomState((prev)=>({
                        ...prev,
                        transform: 'none',
                        active: true,
                        duration: isInitial ? 0 : prev.duration
                    }));
                setActiveContentFP([
                    fPContent
                ]);
            }, timeout);
            return;
        }
        if (fP.attrib.zoomfocus === 'true') {
            let zoom = 1 + parseFloat((fP.attrib?.zoomlevel || '100%').replace('%', '')) / 100;
            let left = parseFloat(fP.attrib?.left.replace('%', ''));
            let top = parseFloat(fP.attrib?.top.replace('%', ''));
            let width = parseFloat(fP.attrib?.size.replace('%', ''));
            // Feste Origin bei 50% 50% belassen und nur per translate + scale zentrieren!
            let offsetX = (50 - left) * zoom;
            let offsetY = (50 - top) * zoom;
            setTimeout(()=>{
                setZoomState((prev)=>({
                        ...prev,
                        active: true,
                        origin: '50% 50%',
                        transform: `translate(${offsetX}%, ${offsetY}%) scale(${zoom})`,
                        duration: isInitial ? 0 : fP.attrib.duration || 2
                    }));
                setActiveContentFP([
                    fPContent
                ]);
            }, timeout);
        }
    };
    useEffect(()=>{
        if (currentFPIndex === undefined) {
            zoomToFocusPoint(undefined);
            prevFPIndexRef.current = currentFPIndex;
            return;
        }
        // Wait for real dimensions before calculating zoom
        if (dimensions && dimensions.width > 0 && dimensions.height > 0) {
            let isDimChangeOnly = prevFPIndexRef.current === currentFPIndex && !isInitialActive;
            let currentFP = focusPoints[currentFPIndex];
            zoomToFocusPoint(currentFP, currentFPIndex, isInitialActive || isDimChangeOnly);
            if (isInitialActive) setIsInitialActive(false);
            prevFPIndexRef.current = currentFPIndex;
        }
    }, [
        currentFPIndex,
        dimensions?.width,
        dimensions?.height
    ]);
    const handleFocusClick = (e, fP, id)=>{
        e.stopPropagation();
        if (currentFPIndex === id) {
            setCurrentFPIndex(undefined);
        } else {
            setCurrentFPIndex(id);
        }
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: focusPoints.map((fP, i)=>{
            let size = parseFloat(fP.attrib.size?.replace('%', ''));
            let height = 'auto';
            let width = fP.attrib.size;
            let minHeight = dimensions && size ? dimensions.width * size / 200 : 'auto';
            let hasGraphicChild = hasChildTag(fP.children, 'Graphic', 3);
            if (fP.attrib.zoomfocus === 'true') {
                width = fP.attrib.size;
            // height = `${widthPx * aspectRatio}px`
            }
            if (hasGraphicChild) {
                width = fP.attrib.size;
            // height = 'auto'
            }
            let focusContent = getChildrenByTag(fP.children, 'FocusPointContent', 2);
            let opacity = undefined;
            if (zoomState.active) {
                let activeFPIsZoom = focusPoints[currentFPIndex]?.attrib.zoomfocus === 'true';
                if (activeFPIsZoom) {
                    opacity = 0; // hide all focus points while zoomed in
                } else if (currentFPIndex !== i) {
                    opacity = 0.3;
                }
            }
            // let filteredFP = removeFPContent(fP.children)
            let isInteractive = fP.attrib.zoomfocus || focusContent.length > 0;
            let transition = zoomState.active ? '300ms ease-out' : `500ms ease-in ${duration}s`;
            return /*#__PURE__*/ _jsx("div", {
                id: `focus-${id ? id : Math.floor(Math.random() * 10)}-${i}`,
                className: clsx(S.focusPointWrap, isInteractive && S.zoomable, zoomState.active && S.active, hasGraphicChild && S.hasGraphic),
                onClick: (e)=>isInteractive && handleFocusClick(e, fP, i),
                style: {
                    top: `${fP.attrib.top}`,
                    left: `${fP.attrib.left}`,
                    width,
                    minHeight: minHeight,
                    ...fP.attrib.pulseColor ? {
                        '--widget_fP_bgColor': fP.attrib.pulseColor
                    } : {},
                    fontSize: fP.attrib.fontsize,
                    height,
                    zIndex: currentFPIndex === i && fP.attrib.alwaysOnTop === 'true' ? 9 : 0,
                    ...opacity !== undefined ? {
                        opacity
                    } : {},
                    transition: `opacity ${transition}, transform ${transition}, filter ${transition}`
                },
                children: applyTemplates(fP)
            }, i);
        })
    });
};
export const FocusPointContentMatcher = TagMatcher(// Child of FocusPoint
// because of dependency, most code is in:
// Graphic -> Image -> FocusPoints
'FocusPointContent', ({ applyTemplates, parentEl, ...attrs })=>{
    let schema = useWidgetSchema();
    let meta = validateMeta('FocusPointContent', attrs, schema);
    let { duration, top, left, width, color, borderRadius, backgroundColor, padding, relativeToFocus } = meta;
    let { mobile } = useWikiContext();
    let styles;
    if (!mobile) {
        const pAttr = parentEl?.tag === 'FocusPoint' && parentEl.attrib || {};
        const toPerc = (val)=>val ? val : '0%';
        let finalTop = toPerc(top);
        let finalLeft = toPerc(left);
        if (relativeToFocus) {
            let pL = toPerc(pAttr.left);
            let pT = toPerc(pAttr.top);
            let pW = toPerc(pAttr.width || 0);
            finalLeft = `calc(${pL} + (${pW} / 2) + ${finalLeft})`;
            finalTop = `calc(${pT} + ${finalTop})`;
        } else if (!top && !left) {
            // Fallback: use parent position
            finalTop = toPerc(pAttr.top);
            finalLeft = toPerc(pAttr.left);
        }
        styles = {
            position: 'absolute',
            '--widget_fPContent_top': finalTop,
            '--widget_fPContent_color': color,
            '--widget_fPContent_left': finalLeft,
            '--widget_fPContent_width': toPerc(width)
        };
    }
    return /*#__PURE__*/ _jsx("div", {
        className: clsx(S.focusPointContent, mobile && S.mobile),
        style: {
            ...styles,
            '--widget_fPContent_padding': !mobile && padding,
            '--widget_fPContent_borderRadius': borderRadius,
            '--widget_fPContent_bgColor': backgroundColor,
            animationDelay: `${duration}s`
        },
        children: applyTemplates()
    }, Math.random());
}, {}, 'Content that gets revealed after FocusPoint Action');
export const FocusPointMatcher = TagMatcher(// because of dependency, most code is in:
// Graphic -> Image -> FocusPoints
'FocusPoint', ({ applyTemplates, childEls })=>{
    // FocusPointContent rendered in Graphic
    let filteredEls = childEls.filter((el)=>el.tag !== 'FocusPointContent');
    return /*#__PURE__*/ _jsx("div", {
        className: S.focusPoint,
        children: applyTemplates(filteredEls)
    });
}, {}, 'Relative Element in Graphic');
export const CarouselMatcher = TagMatcher('Carousel', ({ applyTemplates, childEls, refid, ...props })=>{
    return /*#__PURE__*/ _jsx(Carousel, {
        applyTemplates: applyTemplates,
        ...props,
        children: childEls
    });
}, {}, '');
export const CarouselSlideMatcher = TagMatcher('CarouselSlide', ({ applyTemplates, childEls })=>{
    // TODO: pass refid on
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: applyTemplates(childEls)
    });
}, 'Slide for Carousel');
const Carousel = ({ children, captionInfo, applyTemplates, className, indicatorType, indicatorPos, showNavigationArrows = true, style, refid })=>{
    let [currentIndex, setCurrentIndex] = useState(1);
    let [transitioning, setTransitioning] = useState(false);
    let [resetting, setResetting] = useState(false);
    let totalChildren = children.length;
    let { mobile } = useWikiContext();
    let { caption, linkid, url } = captionInfo || {};
    let captionElement = useCaptionLink(caption, linkid, url, null, true);
    const slides = [
        children[totalChildren - 1],
        ...children,
        children[0]
    ];
    const goNext = ()=>{
        if (!transitioning && !resetting) {
            setTransitioning(true);
            setCurrentIndex((prev)=>prev + 1);
        }
    };
    const goPrev = ()=>{
        if (!transitioning && !resetting) {
            setTransitioning(true);
            setCurrentIndex((prev)=>prev - 1);
        }
    };
    const goTo = (index)=>{
        if (transitioning || resetting || getActualIndex() === index) return;
        setTransitioning(true);
        setCurrentIndex(index + 1);
    };
    const handleTransitionEnd = ()=>{
        if (currentIndex === 0) {
            setResetting(true);
            setTransitioning(false);
            requestAnimationFrame(()=>{
                setCurrentIndex(totalChildren);
                requestAnimationFrame(()=>setResetting(false));
            });
        } else if (currentIndex === totalChildren + 1) {
            setResetting(true);
            setTransitioning(false);
            requestAnimationFrame(()=>{
                setCurrentIndex(1);
                requestAnimationFrame(()=>setResetting(false));
            });
        } else {
            setTransitioning(false);
        }
    };
    const getActualIndex = ()=>{
        if (currentIndex === 0) return totalChildren - 1;
        if (currentIndex === totalChildren + 1) return 0;
        return currentIndex - 1;
    };
    const { isDragging, dragOffset, swipeHandlers } = useSwipeGesture({
        onSwipeLeft: goNext,
        onSwipeRight: goPrev
    }, {
        disabled: transitioning || resetting
    });
    let indicators = /*#__PURE__*/ _jsx("div", {
        className: clsx(S.carouselIndicators, indicatorType === 'circular' && S.circular),
        children: [
            ...Array(totalChildren)
        ].map((_, index)=>/*#__PURE__*/ _jsx("div", {
                className: clsx(S.indicator, {
                    [S.active]: index === getActualIndex()
                }),
                onClick: ()=>goTo(index)
            }, index))
    });
    let navButtonStyle = {
        color: 'white',
        border: '1px solid transparent'
    };
    let navButtons = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Button, {
                className: clsx(S.carouselButton, S.prev),
                onClick: goPrev,
                size: "tiny",
                inverted: true,
                style: navButtonStyle,
                icon: "chevron left"
            }),
            /*#__PURE__*/ _jsx(Button, {
                className: clsx(S.carouselButton, S.next),
                onClick: goNext,
                size: "tiny",
                inverted: true,
                style: navButtonStyle,
                icon: "chevron right"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        refid: refid,
        style: style,
        className: S.carouselWrap,
        children: [
            showNavigationArrows && /*#__PURE__*/ _jsx("div", {
                className: S.carouselNav,
                children: navButtons
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: clsx(S.carouselContainer, className, mobile && S.mobile),
                children: [
                    indicatorPos !== 'bottom' && indicators,
                    /*#__PURE__*/ _jsx("div", {
                        className: clsx(S.carousel, indicatorPos === 'bottom' && S.bottomIndicator),
                        style: {
                            transform: `translateX(calc(-${currentIndex * 100}% + ${isDragging ? dragOffset : 0}px))`,
                            transition: transitioning ? 'transform 0.4s ease' : 'none',
                            cursor: isDragging ? 'grabbing' : 'grab',
                            touchAction: 'pan-y'
                        },
                        onTransitionEnd: handleTransitionEnd,
                        ...swipeHandlers,
                        children: slides.map((child, index)=>/*#__PURE__*/ _jsx("div", {
                                className: S.carouselItem,
                                children: applyTemplates(child)
                            }, index))
                    }),
                    indicatorPos === 'bottom' && indicators,
                    caption && /*#__PURE__*/ _jsx("div", {
                        className: clsx(S.caption, mobile && S.mobile),
                        children: captionElement
                    })
                ]
            })
        ]
    });
};
export const GraphicGroupMatcher = TagMatcher('GraphicGroup', ({ applyTemplates, childEls, refid, ...attrs })=>{
    let schema = useWidgetSchema();
    let meta = validateMeta('GraphicGroup', attrs, schema, refid);
    let { // [content]
    showCaption, caption, linkid, url, // [styling]
    type, align, gap, carouselIndicatorType, carouselIndicatorPos, style } = meta;
    let { mobile } = useWikiContext();
    let margin = mobile ? '1em 0' : null;
    let captionInfo = showCaption && caption && {
        caption,
        linkid,
        url
    };
    let captionEl = useCaptionLink(caption, linkid, url, null, true);
    let renderedChildren = applyTemplates(childEls);
    if (type === 'masonry' && childEls.length >= 1) {
        let colCount = mobile ? 2 : childEls.length === 1 ? 1 : childEls.length === 2 ? 2 : 3;
        let columns = range(colCount).map(()=>[]);
        childEls.forEach((child, i)=>{
            columns[i % colCount].push(child);
        });
        renderedChildren = columns.map((col, i)=>/*#__PURE__*/ _jsx("div", {
                className: S.masonryColumn,
                children: applyTemplates(col)
            }, i));
    }
    if (type === 'carousel') {
        return /*#__PURE__*/ _jsx(Carousel, {
            refid: refid,
            className: clsx(S.graphicGroupContainer, 'presGraphicGroup'),
            children: childEls,
            indicatorPos: carouselIndicatorPos,
            indicatorType: carouselIndicatorType,
            captionInfo: captionInfo,
            style: style,
            applyTemplates: applyTemplates
        });
    } else {
        return /*#__PURE__*/ _jsxs("div", {
            refid: refid,
            className: clsx(S.graphicGroupContainer, 'presGraphicGroup', mobile && S.mobile),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        '--widget_graphicGroup_margin': margin,
                        '--widget_graphicGroup_align': align,
                        '--widget_graphicGroup_gap': gap,
                        ...style
                    },
                    className: clsx(S.graphicGroup, type === 'masonry' && S.masonry, type === 'flex-fill' && S.fill),
                    children: renderedChildren
                }),
                captionInfo && /*#__PURE__*/ _jsx("div", {
                    className: clsx(S.caption, mobile && S.mobile),
                    children: captionEl
                })
            ]
        });
    }
}, {}, 'Renders a Group of Graphics');
export const GridElement = TagMatcher('GridElement', ({ applyTemplates, position })=>{
    return /*#__PURE__*/ _jsx("div", {
        className: S[position],
        children: applyTemplates()
    });
}, {
    position: [
        'top-left',
        'top-center',
        'top-right',
        'center-left',
        'center-center',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right'
    ]
});
export const Quote = TagMatcher('Quote', ({ applyTemplates, url, caption, linkid, margin, padding, marginLeft, marginRight, marginBottom, marginTop, pageid, type, refid })=>{
    let [ref, inView] = useInView({
        triggerOnce: true
    });
    let { mobile } = useWikiContext();
    let md_caption = usePageIdMdCaption(inView && !caption ? pageid : null);
    if (pageid && !caption) {
        caption = md_caption;
    }
    let quoteCaption = useCaptionLink(caption, linkid, url, null, true);
    let cntPadding = mobile && '1em 0';
    return /*#__PURE__*/ _jsxs("div", {
        refid: refid,
        style: {
            margin,
            padding: cntPadding,
            marginLeft,
            marginRight,
            marginBottom,
            marginTop
        },
        className: clsx(S.quoteContainer, 'presQuoteCnt', type == 'minimal' && S.minimal, type == 'inverted' && S.inverted, type == 'accent' && S.accent),
        ref: ref,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    padding
                },
                className: clsx(S.quote, 'presQuote'),
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: S.symbol,
                        children: "“"
                    }),
                    applyTemplates()
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: S.caption,
                children: quoteCaption
            })
        ]
    });
}, {
    type: [
        'default',
        'minimal'
    ],
    linkid: '',
    caption: '',
    url: '',
    padding: '2em',
    margin: '1em'
});
export const More = TagMatcher('More', ({ applyTemplates, text })=>{
    let [show, setShow] = useState(false);
    let [measuredHeight, setMeasuredHeight] = useState(0);
    let { mobile } = useWikiContext();
    let toggleMore = ()=>{
        setShow(!show);
    };
    let [dimensions, refDimensions] = useDimensions({
        defaults: null
    });
    useEffect(()=>{
        if (dimensions) {
            const h = dimensions.height;
            setMeasuredHeight(h);
        }
    }, [
        applyTemplates,
        dimensions
    ]);
    let moreStyle = measuredHeight > 0 ? {
        position: 'relative',
        opacity: '1',
        '--more_end_height': `calc(${measuredHeight}px + 1em)`,
        '--more_start_height': '0px'
    } : null;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: clsx(S.moreContainer, text && S.textShown, show && S.show, 'presMore', mobile && S.mobile),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        width: '100%',
                        maxHeight: '0px',
                        overflow: 'hidden',
                        position: 'absolute'
                    },
                    children: /*#__PURE__*/ _jsx("div", {
                        ref: refDimensions,
                        className: S.more,
                        style: {
                            position: 'absolute',
                            visibility: 'hidden',
                            pointerEvents: 'none',
                            height: 'auto'
                        },
                        children: applyTemplates()
                    })
                }),
                !text && /*#__PURE__*/ _jsx(Button, {
                    className: S.moreButton,
                    onClick: toggleMore,
                    size: "tiny",
                    inverted: true,
                    style: {
                        color: 'var(--presentation_themeColor)',
                        backgroundColor: 'var(--presentation_themeColor_backdrop)',
                        border: '1px solid var(--presentation_themeColor)'
                    },
                    icon: show ? 'minus' : 'plus'
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: S.indicator,
                    children: [
                        text && /*#__PURE__*/ _jsx("span", {
                            onClick: toggleMore,
                            className: S.text,
                            children: text
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            onClick: toggleMore,
                            className: S.line
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: S.lineBottom
                }),
                /*#__PURE__*/ _jsx("div", {
                    style: moreStyle,
                    className: S.more,
                    children: applyTemplates()
                })
            ]
        })
    });
}, {});
export const Column = TagMatcher('Column', ({ applyTemplates, childEls })=>{
    // handled in parent Columns component
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: applyTemplates(childEls)
    });
}, 'Column for Columns');
export const Columns = TagMatcher('Columns', ({ applyTemplates, width, gap, childEls, style, refid, imageSource })=>{
    let { mobile } = useWikiContext();
    let apiurl = useSelector(Sel.apiurl);
    let id = isNaN(imageSource) ? null : imageSource;
    let imgsrc = id && useImageInfo({
        id
    }).imgsrc;
    let backgroundImage = null;
    if (imgsrc || id) {
        backgroundImage = imgsrc ? `url(${imgsrc})` : `url(${apiurl.href(imageSource)})`;
    }
    // distribute even columns or use width
    let widthArray = width ? String(width).split(',').filter((s)=>isDigit(strip(s))).map((s)=>parseInt(s)) : Array(childEls.length).fill(Math.floor(100 / childEls.length));
    // fill up missing widths (distribute remaining percentage evenly)
    let unset = 100 - sum(widthArray);
    let delta = childEls.length - widthArray.length;
    if (delta > 0) {
        widthArray = [
            ...widthArray,
            ...range(delta).map((i)=>Math.floor(unset / delta))
        ];
    }
    let gapValue = typeof gap === 'string' ? gap : `${gap}px`;
    let children = childEls.map((childEl, i)=>{
        let width = widthArray[i];
        let clmnStyle = {
            flex: `1 0 calc(${width}% - ${gapValue})`
        };
        if (mobile) {
            clmnStyle = {
                flex: '100%'
            };
        }
        let columnStyle = {
            ...clmnStyle,
            ...childEl.attrib.style
        };
        return /*#__PURE__*/ _jsx("div", {
            refid: childEl.attrib.refid,
            className: clsx(S.column, '.presColumn'),
            style: columnStyle,
            children: applyTemplates(childEl)
        }, i);
    });
    let newStyle = {
        ...style,
        gap: gapValue,
        ...backgroundImage && {
            backgroundImage,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        }
    };
    return /*#__PURE__*/ _jsx("div", {
        refid: refid,
        style: newStyle,
        className: clsx(S.columns, mobile && S.mobile, 'presColumns'),
        children: children
    });
}, 'Creates Column Layout, takes direct children as column elements');
const TextTickerControl = ({ wikipaneId, audio, audioDelay, ...props })=>{
    /* control visibility/loading and combines a TextTicker with a AudioTicker
   */ let { mobile } = useWikiContext();
    let [ref, inView] = useInView({
        triggerOnce: false
    });
    let [playerState, setPlayerState] = useState({
        muted: false,
        playing: false,
        ended: false
    });
    let { playing } = playerState;
    let setPlayer = (props)=>{
        return setPlayerState({
            ...playerState,
            ...props
        });
    };
    useEffect(()=>{
        if (!audio) {
            setPlayer({
                playing: true
            });
        }
        if (!inView) {
            setPlayer({
                playing: false
            });
        }
    }, [
        inView
    ]);
    let playHandler = ()=>{
        setPlayer({
            playing: true
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: S.tickerControl,
                children: audio && /*#__PURE__*/ _jsx(AudioTicker, {
                    inView: inView,
                    audioDelay: audioDelay,
                    started: inView && playing,
                    audio: inView && audio,
                    playerState: playerState,
                    setPlayerState: setPlayer
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    position: 'relative',
                    clear: 'both'
                },
                ref: ref,
                children: [
                    !playing && inView && /*#__PURE__*/ _jsx("div", {
                        className: S.overlay,
                        children: /*#__PURE__*/ _jsx(Button, {
                            style: {
                                color: 'var(--presentation_themeColor)',
                                border: '1px solid var(--presentation_themeColor)'
                            },
                            size: "huge",
                            inverted: true,
                            icon: "play",
                            circle: true,
                            onClick: playHandler
                        })
                    }),
                    /*#__PURE__*/ _jsx(TextTicker, {
                        playing: playing,
                        audio: audio,
                        mobile: mobile,
                        started: inView && playing,
                        viewerId: wikipaneId,
                        ...props
                    })
                ]
            })
        ]
    });
};
const DOTS = String.fromCodePoint(0x2026);
const defaultsTicker = {
    id: null,
    startIndex: 0,
    words: null,
    stopIndex: null,
    pauses: null,
    sep: ` ${DOTS} `
};
// only for auto complete setup, is manually handled in TextTickerMatcher
export const TickerMatcher = TagMatcher('TickerPage', ()=>null);
// XXX: child relation is not handled since schema derived completion
//~ completion: () => ({
//~ '!top': ['TextTicker'],
//~ TextTicker: {
//~ attrs: decodeCompletionAttrs(defaultsTextTicker),
//~ children: ['TickerPage', 'Description'],
//~ }),
export const TextTickerMatcher = {
    tagMatcher: 'TextTicker',
    match: ({ tag })=>tag == 'TextTicker',
    build: ({ attrib, children, applyTemplates, wikipaneId, templates })=>{
        let descrEl = children.find((e)=>e.tag == 'Description');
        let renderedDescr = descrEl ? [
            descrEl.text,
            applyTemplates(descrEl.children)
        ] : null;
        let template = templates.TickerMatcher;
        let sequences = children.filter((e)=>e.tag == 'TickerPage' && isDigit(e.attrib.id)).map((e)=>({
                otid: e.attrib.id,
                ...schemaifyAttributes(e.attrib, template.schema)
            }));
        return /*#__PURE__*/ _jsx(TextTickerControl, {
            description: renderedDescr,
            sequences: sequences,
            ...attrib,
            wikipaneId: wikipaneId
        });
    }
};
export const AiImageMatcher = TagMatcher('AiImage', AiImage, {
    seed: '',
    width: '',
    height: ''
}, 'OpenAi Image');
export const ShowcaseMatcher = TagMatcher('Showcase', ({ id, ids, fontsize, carousel, ...attr })=>{
    return /*#__PURE__*/ _jsx(Showcase, {
        otid: id,
        otids: ids,
        fontSize: fontsize,
        carousel: carousel,
        ...attr
    });
}, {
    linkid: '',
    id: '',
    gap: '24',
    fontsize: '18px',
    caption: ''
}, 'Showcase by id');
export const CustomCSSMatcher = TagMatcher('CustomCSS', ({ text, childEls, wikipaneId })=>{
    let cssCode = childEls.filter((e)=>e.tag == 'br').map((e)=>e.tail).join('');
    return /*#__PURE__*/ _jsxs(Style, {
        type: "text/css",
        id: wikipaneId ? `presentation-css-${wikipaneId}` : 'presentation-css',
        children: [
            cssCode,
            text
        ]
    });
}, {}, 'Provides html-head with some additional <style/>');
export const UserScript = TagMatcher('UserScript', ({ text })=>{
    useEffect(()=>{
        if (!text) return;
        let script = document.head.querySelector('#presentation-user-script');
        if (!script) {
            script = document.createElement('script');
            script.id = 'presentation-user-script';
            script.type = 'text/javascript';
            document.head.appendChild(script);
        }
        try {
            new Function(text);
        } catch (err) {
            log.warn('UserScript: invalid script content', err);
            return;
        }
        script.textContent = text;
    }, [
        text
    ]);
    return null;
}, {}, 'Provides html-head with some additional <script/>');
export const MenuItemMatcher = TagMatcher('MenuItem', ({ parentEl, applyTemplates, childEls, caption, showCaption, linkid, url, external, icon, style, position, sidebar, sidebarDimmed, imageSource })=>{
    let subMenuItems = childEls ? childEls.filter((e)=>e.tag === 'MenuItem') : [];
    let otherChildEls = childEls ? childEls.filter((e)=>e.tag !== 'MenuItem') : [];
    let [visible, setVisible] = useState(false);
    let [animating, setAnimating] = useState(false);
    let menuRef = useRef(null);
    let apiurl = useSelector(Sel.apiurl);
    let handleClick = (e)=>{
        e.preventDefault(); // verhindert ungewolltes Navigieren
        if (visible) {
            handleClose();
        } else {
            setVisible(true);
            requestAnimationFrame(()=>setAnimating(true));
        }
    };
    let handleClose = ()=>{
        setAnimating(false); // zurück animieren
        setTimeout(()=>setVisible(false), 300); // nach Animation aus DOM
    };
    // close sidebar when clicking/tapping outside of it
    useEffect(()=>{
        const handleOutside = (e)=>{
            if (!visible) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            handleClose();
        };
        document.addEventListener('click', handleOutside, true);
        document.addEventListener('touchstart', handleOutside, true);
        return ()=>{
            document.removeEventListener('click', handleOutside, true);
            document.removeEventListener('touchstart', handleOutside, true);
        };
    }, [
        visible
    ]);
    let image = null;
    if (imageSource && imageSource !== 'undefined') {
        image = `${apiurl.href(imageSource)}`;
    }
    let content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            icon && /*#__PURE__*/ _jsx(VIcon, {
                name: icon
            }),
            !image && showCaption && caption,
            image && /*#__PURE__*/ _jsx("img", {
                width: "100%",
                height: "auto",
                src: image
            }),
            !sidebar && otherChildEls.length > 0 && applyTemplates(otherChildEls),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    background: 'var(--presentation_themeColor)'
                }
            })
        ]
    });
    let isSubItem = parentEl?.tag === 'MenuItem';
    let itemStyle = {
        position: 'relative',
        color: 'var(--presentation_themeColor)',
        maxWidth: image ? '200px' : null
    };
    let subItemStyle = {
        display: 'block',
        margin: '0',
        padding: '1em',
        borderRadius: 0,
        color: `var('--presentation_themeColor_content')`
    };
    let combinedStyle = {
        ...itemStyle,
        ...isSubItem ? subItemStyle : {},
        ...style
    };
    let menuItem = sidebar ? /*#__PURE__*/ _jsx("button", {
        type: "button",
        style: {
            appearance: 'none',
            background: 'none',
            border: 0,
            font: 'inherit',
            ...combinedStyle
        },
        onClick: handleClick,
        "aria-expanded": visible,
        className: clsx(S.sidebarMenuButton, visible ? 'active' : null),
        children: content
    }) : /*#__PURE__*/ _jsx(Link, {
        id: linkid,
        path: !linkid ? url : undefined,
        external: external,
        target: external ? '_blank' : undefined,
        style: combinedStyle,
        className: clsx(visible ? 'active' : null),
        children: content
    });
    if (subMenuItems.length > 0) {
        menuItem = /*#__PURE__*/ _jsx(Dropdown, {
            item: true,
            trigger: menuItem,
            icon: null,
            on: "hover",
            children: /*#__PURE__*/ _jsx(Dropdown.Menu, {
                style: {
                    padding: 0,
                    border: 'none',
                    background: `var(--presentation_themeColor_backdrop)`,
                    boxShadow: 'none'
                },
                children: applyTemplates(subMenuItems)
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            menuItem,
            sidebar && visible && /*#__PURE__*/ _jsx(AutoPortal, {
                target: "presentationViewport",
                children: /*#__PURE__*/ _jsxs("div", {
                    ref: menuRef,
                    className: S.menuSidebar,
                    children: [
                        sidebarDimmed && /*#__PURE__*/ _jsx("div", {
                            className: clsx(S.overlay, animating && S.visible),
                            onClick: handleClose
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: clsx(S.sidebar, animating && S.open, position === 'left' && S.left, position === 'right' && S.right),
                            children: otherChildEls.length > 0 ? applyTemplates(otherChildEls) : applyTemplates()
                        })
                    ]
                })
            })
        ]
    });
}, {});
const ExPopup = ({ content, trigger, name, id, type, href })=>{
    let apiurl = useSelector(Sel.apiurl);
    let typeIcon = {
        Subject: 'web_subject',
        Place: 'web_place',
        Name: 'web_person',
        Title: 'web_title',
        GlossaryItem: 'glossary'
    }[type] || 'link';
    return /*#__PURE__*/ _jsx(XPopup, {
        fitted: true,
        basic: true,
        wide: true,
        hoverable: true,
        position: "left center",
        on: "hover",
        mouseEnterDelay: 500,
        trigger: trigger,
        children: /*#__PURE__*/ _jsxs(Card, {
            fluid: true,
            style: {
                maxWidth: '300px'
            },
            children: [
                name && /*#__PURE__*/ _jsx(CardHeader, {
                    style: {
                        padding: '0.3rem 0.8rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        verticalAlign: 'middle'
                    },
                    children: /*#__PURE__*/ _jsxs(Header, {
                        style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center'
                        },
                        as: "h3",
                        children: [
                            name,
                            /*#__PURE__*/ _jsx(VIcon, {
                                style: {
                                    display: 'inline-block',
                                    marginLeft: 'auto',
                                    fontSize: '1em'
                                },
                                name: typeIcon,
                                href: href || apiurl.baseUrl('/' + id),
                                target: "_blank"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(CardContent, {
                    children: content
                })
            ]
        })
    });
};
const OutgoingExhibitionLink = (type)=>TagMatcher(`Ex${type}`, ({ id, title, linkIcon, linkicon, applyTemplates, abstract, name })=>{
        let apiurl = useSelector(Sel.apiurl);
        let content = /*#__PURE__*/ _jsx(RawLink, {
            style: {
                display: 'inline-block'
            },
            linkIcon: linkIcon || linkicon,
            href: `${apiurl}/${id}`,
            target: "_blank",
            title: title,
            children: applyTemplates()
        });
        if (id && abstract) {
            return /*#__PURE__*/ _jsx(ExPopup, {
                trigger: content,
                name: name,
                content: abstract,
                id: id,
                type: type
            });
        } else return content;
    }, {
        id: '',
        linkIcon: false
    }, `Outgoing ${type} link`);
export const ExTitleMatcher = OutgoingExhibitionLink('Title');
export const ExPageMatcher = OutgoingExhibitionLink('Page');
export const ExPersonMatcher = OutgoingExhibitionLink('Person');
export const ExPlaceMatcher = OutgoingExhibitionLink('Place');
export const ExSubjectMatcher = OutgoingExhibitionLink('Subject');
export const ExGlossaryItemMatcher = OutgoingExhibitionLink('GlossaryItem');
export const CaptionMatcher = TagMatcher('Description', ({ applyTemplates, linkid, caption })=>{
    let { mobile } = useWikiContext();
    let content = caption ? useCaptionLink(caption, linkid) : applyTemplates();
    return /*#__PURE__*/ _jsx("div", {
        className: clsx(S.caption, mobile && S.mobile),
        children: /*#__PURE__*/ _jsx("div", {
            className: GlobalS.caption,
            children: content
        })
    });
}, {
    linkid: '',
    caption: ''
}, 'Creates and styles (caption) description element');
export const ImageViewerMatcher = TagMatcher('ImageViewer', ({ applyTemplates, childEls, ...props })=>{
    let data_opts = {};
    let { mobile } = useWikiContext();
    let { id, ids, ...more_opts } = props;
    if (id) {
        data_opts = {
            ...data_opts,
            otid: parseInt(id)
        };
    }
    if (ids) {
        let otids = String(ids).split(',').map((otid)=>parseInt(otid)).filter(Boolean);
        data_opts = {
            ...data_opts,
            otids
        };
    }
    let { id: otid, ids: otids, ...opts } = more_opts;
    let p = {
        ...data_opts,
        ...opts
    };
    return /*#__PURE__*/ _jsx(PageViewer, {
        ...p,
        mobile: mobile
    });
}, 'Page viewer for a given root id');
export const TransitionMatcher = TagMatcher('Transition', ({ applyTemplates, ...attrs })=>{
    // not worth to improve?
    // accepts only GraphicWidget in VLM (used by tmw decolonial)
    // => should be feature of scenes to enable on whole content ?
    let schema = useWidgetSchema();
    let meta = validateMeta('Transition', attrs, schema);
    return /*#__PURE__*/ _jsx(InViewTransition, {
        ...meta,
        children: applyTemplates()
    });
}, 'Transition on children in View');
export const ModalContentMatcher = TagMatcher('ModalContent', ({ applyTemplates })=>applyTemplates(), ()=>({} // tag completion only via ModalMatcher
    ));
export const IFrameMatcher = TagMatcher('iframe', ({ width, height, src })=>{
    let [allowed, setAllowed] = useState(false);
    if (!allowed) {
        return /*#__PURE__*/ _jsxs("div", {
            className: S.iframeDisclaimer,
            style: {
                width: width,
                height: height
            },
            children: [
                /*#__PURE__*/ _jsx("p", {
                    children: "An dieser Stelle werden externe Ressourcen geladen. Bitte erlauben Sie dies, um fortzufahren."
                }),
                /*#__PURE__*/ _jsx(Button, {
                    inverted: true,
                    className: S.button,
                    circle: false,
                    onClick: ()=>setAllowed(true),
                    children: "Externen Inhalt laden"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx("iframe", {
        src: src,
        height: height,
        width: width
    });
});
const ModalWrapper = ({ open, onClose, caption, mobile, scrolling, bgid, modalContent, applyTemplates, ...props })=>{
    if (!open) return null;
    return /*#__PURE__*/ _jsx(Modal, {
        caption: caption,
        mobile: mobile,
        open: open,
        scrolling: scrolling,
        imageId: bgid,
        onClose: onClose,
        fullwidth: false,
        ...props,
        children: modalContent ? applyTemplates(modalContent) : /*#__PURE__*/ _jsx(Message, {
            children: "no ModalContent"
        })
    });
};
export const ModalMatcher = TagMatcher('Modal', ({ applyTemplates, text, caption, linkid, childEls, inline = true, bgid, linkIcon, scrolling, refid, ...props })=>{
    let [modalOpen, setModalOpen] = useState(false);
    let { openModal, closeModal } = useModalWidSync(refid, modalOpen, setModalOpen);
    let contentEls = childEls.filter((e)=>e.tag != 'ModalContent');
    let modalContent = childEls.filter((e)=>e.tag == 'ModalContent').at(0);
    let { mobile } = useWikiContext();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ModalWrapper, {
                open: modalOpen,
                caption: useCaptionLink(caption, linkid),
                mobile: mobile,
                scrolling: scrolling,
                bgid: bgid,
                onClose: closeModal,
                modalContent: modalContent,
                applyTemplates: applyTemplates,
                ...props
            }),
            /*#__PURE__*/ _jsx("div", {
                refid: refid,
                style: {
                    cursor: 'pointer',
                    display: inline ? 'contents' : 'block'
                },
                onClick: openModal,
                children: /*#__PURE__*/ _jsxs("a", {
                    children: [
                        [
                            text || caption,
                            applyTemplates(contentEls)
                        ],
                        ' ',
                        linkIcon && /*#__PURE__*/ _jsx(VIcon, {
                            style: {
                                fontSize: '.75em'
                            },
                            name: "link"
                        })
                    ]
                })
            })
        ]
    });
}, ()=>({
        '!top': [
            'Modal'
        ],
        Modal: {
            attrs: {
                inline: BOOL_STR,
                bgid: '',
                scrolling: true,
                hideCaption: false,
                caption: '',
                linkIcon: false,
                type: [
                    'zoom',
                    'viewer',
                    'text'
                ]
            },
            children: [
                'ModalContent'
            ]
        },
        ModalContent: {
            attrs: {}
        }
    }));
export const TextBlock = TagMatcher('TextBlock', ({ applyTemplates, borderRadius, fixed, top, left, bottom, right, width, textAlign, fontSize, contentColor, padding, backgroundColor, backgroundBlur, mobileAbsolute, imageSource, refid })=>{
    let { mobile } = useWikiContext();
    let addStyles = {};
    let apiurl = useSelector(Sel.apiurl);
    let id = isNaN(imageSource) ? null : imageSource;
    let imgsrc = id && useImageInfo({
        id
    }).imgsrc;
    let backgroundImage = null;
    if (imgsrc || id) {
        backgroundImage = imgsrc ? `url(${imgsrc})` : `url(${apiurl.href(imageSource)})`;
    }
    let [ref, inView] = useInView();
    let isAbsolute = top || left || bottom || right;
    if (!isAbsolute) {
        addStyles = {
            ...addStyles,
            position: 'relative'
        };
    } else if (mobile) {
        if (!mobileAbsolute) {
            addStyles = {
                ...addStyles,
                position: 'relative',
                top: 'inherit',
                bottom: 'inherit',
                left: 'inherit',
                right: 'inherit',
                width: borderRadius === '100%' ? width : '100%'
            };
        }
    }
    const blurPercentToPx = (percent)=>`${parseFloat(percent) / 100 * 10}px`;
    let blockStyle = {
        top,
        left,
        bottom,
        right,
        width,
        backgroundColor: backgroundImage ? 'transparent' : backgroundColor,
        ...{
            backdropFilter: `blur(${blurPercentToPx(backgroundBlur)})`
        },
        padding,
        borderRadius,
        textAlign,
        fontSize,
        backgroundImage,
        '--widget_textblock_color': contentColor,
        ...addStyles
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                refid: refid,
                style: blockStyle,
                className: clsx(S.textblock, 'presTextblock', fixed && S.fixed, inView && S.visible),
                children: applyTemplates()
            }),
            fixed && /*#__PURE__*/ _jsx("div", {
                ref: ref,
                style: {
                    width: '1px',
                    pointerEvents: 'none',
                    visibility: 'hidden',
                    height: '100%',
                    top: '0',
                    position: 'absolute'
                },
                className: clsx(S.textblock, 'presTextblock')
            })
        ]
    });
}, {
    top: 'inherit',
    left: 'inherit',
    bottom: 'inherit',
    right: 'inherit',
    width: 'auto',
    textalign: 'left',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    fontsize: '0.85em',
    contentcolor: 'inherit',
    padding: '1em',
    borderRadius: '0',
    mobileAbsolute: false
});
export const AudioPlayer = TagMatcher('AudioPlayer', (props)=>{
    return /*#__PURE__*/ _jsx(MediaPlayer.Audio, {
        ...props
    });
}, {});
export const VideoPlayer = TagMatcher('VideoPlayer', (props)=>{
    return /*#__PURE__*/ _jsx(MediaPlayer.Video, {
        ...props
    });
}, {});
export const TagCloudMatcher = TagMatcher('TagCloud', ({ type })=>/*#__PURE__*/ _jsx(TagCloud, {
        className: S.tagcloud,
        toLegacy: true,
        type: type
    }), {
    type: [
        'name',
        'keyword',
        'place'
    ]
});
export const ExternalLinkMatcher = TagMatcher('ExternalLink', ({ href, applyTemplates, linkIcon, linkicon, abstract, name })=>{
    // todo: hack for INTERNAL Links, should be handled earlier in the pipeline!
    let isNumericHref = typeof href === 'number';
    if (isNumericHref) {
        return /*#__PURE__*/ _jsx(Link, {
            id: href,
            children: applyTemplates()
        });
    }
    //GND Temp Solution - ExGndMatcher
    let gndid = null;
    if (typeof href === 'string' && href.includes('explore.gnd.network/gnd/')) {
        gndid = href.split('/').pop();
    }
    let content = /*#__PURE__*/ _jsx(RawLink, {
        linkIcon: linkIcon || linkicon,
        href: href,
        target: "_blank",
        children: applyTemplates()
    });
    if (gndid) {
        return /*#__PURE__*/ _jsx(GndPopup, {
            gndid: gndid,
            trigger: content
        });
    } else if (abstract) {
        return /*#__PURE__*/ _jsx(ExPopup, {
            href: href,
            trigger: content,
            name: name || applyTemplates(),
            content: abstract
        });
    } else {
        return content;
    }
}, {
    href: '',
    linkIcon: false
});
export const SearchTermMatcher = TagMatcher('SearchTerm', ({ text, searchterm })=>{
    let apiurl = useSelector(Sel.apiurl);
    let href = apiurl.href(`/search/quick?query=${searchterm}`);
    return /*#__PURE__*/ _jsx(Button, {
        className: S.searchTag,
        circle: false,
        children: /*#__PURE__*/ _jsxs(RawLink, {
            target: "_blank",
            href: href,
            children: [
                "Mehr zu ",
                text ? text : searchterm
            ]
        })
    });
}, {
    searchterm: ''
});
const SvgAnnotationContext = /*#__PURE__*/ createContext({
    triggerFuncs: {}
});
export const matchSvgAnnotation = {
    match: ({ tag, parent, attrib })=>{
        if (attrib.id?.startsWith('vl-') && tag !== 'AnnotationItem') {
            while(parent){
                if (parent.tag == 'svg') return true;
                // parents of parents are WeakRef objects
                parent = parent instanceof WeakRef ? parent.deref() : parent.parent;
            }
        }
        return false;
    },
    build: ({ tag, attrib, applyTemplates })=>/*#__PURE__*/ _jsx(SvgAnnotationContext.Consumer, {
            children: (ctx)=>/*#__PURE__*/ React.createElement(tag, {
                    ...attrib,
                    className: clsx(attrib.className, {
                        selected: ctx.selectedTarget === attrib.id || ctx.availableTargets?.length > 0 && ctx.availableTargets.every((target)=>target === attrib.id)
                    }, {
                        active: ctx.availableTargets?.includes(attrib.id)
                    }),
                    onClick: ()=>ctx.triggerFuncs(attrib.id),
                    children: applyTemplates(),
                    style: {
                        ...attrib.style,
                        cursor: 'pointer'
                    },
                    fill: attrib.fill || attrib.style?.fill || 'transparent'
                })
        })
};
const ImageWebResource = ({ canvasid, applyTemplates, buildNodeSet })=>{
    /* a SVG-Resource, that is given by a canvasid (probably a webresource 9/2)
  // XXX: maybe support raster images as well
  */ let [ref, inView] = useInView({
        triggerOnce: true
    });
    let { data, error } = useApi('presentation/getSvgAsset', {
        otid: canvasid
    }, {
        condition: inView
    });
    let res = null;
    if (data) {
        if (data.svg) {
            let svg_nodes = buildNodeSet(data.svg);
            res = applyTemplates(svg_nodes);
        } else {
            // raster image resource?
            res = 'bad svg data';
        }
    }
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        children: res
    });
};
export const AnnotationCanvasMatcher = TagMatcher('AnnotationCanvas', ({ util, applyTemplates, childEls, seqmode, canvasid, top, left, width, imageWidth })=>{
    let { mobile } = useWikiContext();
    // selected by user
    let [selectedTarget, setSelectedTarget] = useState(null);
    let [refid, setRefid] = useState(null);
    // active point on timeline
    let [availableTargets, setAvailableTargets] = useState(null);
    // get all Annotation children that have target attrib
    let annotationItems = childEls.filter((e)=>e.tag == 'AnnotationItem' && e.attrib.target);
    //minify annotationItems for less calculation later on
    let annotations = annotationItems.map((e)=>({
            seqidx: e.attrib.seqidx,
            target: e.attrib.target,
            refid: e.attrib.resolved_refid
        }));
    let annotationsByTarget = groupBy(annotations, (e)=>e.target);
    //Timeline:
    let annotationsBySeqIdx = groupBy(annotations, (a)=>a.seqidx);
    let seqidxs = keys(annotationsBySeqIdx);
    let first_seqidx = seqidxs.at(0);
    let last_seqidx = seqidxs.at(-1);
    let emptyCount = 0;
    let even = false;
    const renderTimelineItem = (seqidx)=>{
        if (seqidx > last_seqidx) return null // limits rendering
        ;
        let activeSeq = annotationsBySeqIdx[seqidx] ?? [];
        let selectedSeq = refid ? activeSeq.find((e)=>refid === e.refid) : false;
        let hasContent = activeSeq.length > 0;
        let nextHasContent = annotationsBySeqIdx[seqidx + 1]?.length > 0;
        // activate timelineItem based on:
        // -selected seqidx has only one AnnotationItem,
        // -selected AnnotationItem including this seqidx,
        // or availableTargets including selected seqidx,
        let activeState = refid ? selectedSeq?.refid === refid : activeSeq.some((e)=>selectedTarget === e.target || availableTargets?.includes(e.target));
        //!hasContent: renders empty elements between two sequences
        //even: makes the timelineItem changing vertical position on the timeline
        if (!hasContent && !nextHasContent) {
            emptyCount = emptyCount + 1;
        } else {
            emptyCount = hasContent ? 1 : emptyCount + 1;
            if (hasContent) {
                even = !even;
            }
            return /*#__PURE__*/ _jsx("div", {
                style: {
                    flex: emptyCount
                },
                className: clsx(S.navItemContainer, mobile ? S.mobile : '', hasContent && S.filled, activeState && S.active, !even && S.odd),
                onClick: ()=>{
                    if (hasContent) {
                        setAvailableTargets(activeSeq && activeSeq.map((a)=>a.target));
                        // set selected values when all targets same or one just one selected
                        let sameTarget = activeSeq.every((a)=>a.target === activeSeq[0].target);
                        setSelectedTarget(sameTarget ? activeSeq[0].target : undefined);
                        setRefid(sameTarget ? activeSeq[0].refid : null);
                    }
                },
                children: /*#__PURE__*/ _jsx("div", {
                    className: S.navItem,
                    children: hasContent && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: seqidx
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: S.circle
                            })
                        ]
                    })
                })
            }, seqidx);
        }
    };
    let navigationTimeline = /*#__PURE__*/ _jsx("div", {
        className: S.timeline,
        children: range(first_seqidx, last_seqidx + 1).map(renderTimelineItem)
    });
    let ctx = {
        triggerFuncs: (e)=>(setSelectedTarget(e), setAvailableTargets([]), setRefid(null)),
        selectedTarget: selectedTarget,
        availableTargets: availableTargets
    };
    let canvasElement = null;
    // note: childEls hold only one child, which is the root Element, i.e. <AnnotationCanvas>
    // for now assume that <svg> element is top level child of AnnotationCanvas
    let svg_or_img = childEls.filter((e)=>e.tag == 'svg' || e.tag == 'img');
    if (svg_or_img.length > 0) {
        canvasElement = applyTemplates(svg_or_img);
    } else if (canvasid) {
        canvasElement = /*#__PURE__*/ _jsx(ImageWebResource, {
            canvasid: canvasid,
            applyTemplates: applyTemplates,
            buildNodeSet: util.buildNodeSet
        });
    }
    let currentIndex = annotations.findIndex((e)=>{
        if (refid) {
            return e.refid == refid;
        } else if (selectedTarget) {
            // checks if there is just one target or identical targets (target, seqidx)
            let targets = annotationsByTarget[selectedTarget] ?? [];
            let identicalTargets = targets.every((t)=>t.target === targets[0].target && t.seqidx === targets[0].seqidx);
            return e.target === selectedTarget && (targets.length === 1 || identicalTargets);
        }
    });
    let selectedAnnotation = annotationItems[currentIndex];
    let attrib = selectedAnnotation?.attrib || {};
    let annotationStyles = !mobile ? {
        position: 'absolute',
        top: attrib.top || top,
        left: attrib.left || left,
        width: attrib.width || width
    } : null;
    let goPrev = ()=>{
        if (currentIndex > 0) {
            let prevKey = annotations[currentIndex - 1];
            setAvailableTargets(null);
            setSelectedTarget(prevKey.target);
            setRefid(prevKey.refid);
        }
    };
    let goNext = ()=>{
        if (currentIndex < annotations.length - 1) {
            let nextKey = annotations[currentIndex + 1];
            setSelectedTarget(nextKey.target);
            setAvailableTargets(null);
            setRefid(nextKey.refid);
        }
    };
    let seqNavButtons = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            currentIndex > 0 && /*#__PURE__*/ _jsx(Button, {
                className: clsx(S.carouselButton, S.prev, mobile && S.mobile),
                onClick: goPrev,
                icon: "chevron left"
            }),
            currentIndex < annotations.length - 1 && /*#__PURE__*/ _jsx(Button, {
                className: clsx(S.carouselButton, S.next, mobile && S.mobile),
                onClick: goNext,
                icon: "chevron right"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SvgAnnotationContext.Provider, {
                value: ctx,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: clsx(S.svgContainer, mobile && S.mobile, seqmode && S.seqMode),
                    style: {
                        '--widget_annotationCanvas_width': imageWidth
                    },
                    children: [
                        (seqmode || mobile) && seqNavButtons,
                        canvasElement
                    ]
                })
            }),
            seqmode ? navigationTimeline : null,
            selectedAnnotation && /*#__PURE__*/ _jsxs("div", {
                className: clsx(S.annotation, mobile && S.mobile),
                style: annotationStyles,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: S.header,
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                children: attrib.caption
                            }),
                            !mobile && /*#__PURE__*/ _jsx(Button, {
                                icon: 'close',
                                className: S.closeButton,
                                onClick: ()=>{
                                    setRefid(null);
                                    setSelectedTarget(null);
                                    setAvailableTargets(null);
                                },
                                size: "tiny",
                                inverted: true,
                                style: {
                                    color: 'white',
                                    border: '1px solid white'
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: S.content,
                        children: [
                            selectedAnnotation.text,
                            applyTemplates(selectedAnnotation.children)
                        ]
                    })
                ]
            })
        ]
    });
}, {
    caption: '',
    seqmode: false,
    top: '10%',
    left: '60%',
    width: '40%'
});
